import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStoreHours } from "./hooks/useStoreHours.ts";
import { formatWeeklyHours } from "./utils/formatHours.ts";
import { getPopupAlertForStore } from "./services/popupAlertApiService.ts";
import "./DispensaryInfo.css";
import analyticsService from "./services/AnalyticsService.ts";

interface StoreProps {
    store: {
        id: number;
        name: string;
        address: string;
        phone?: string;
        isMedical?: boolean;
        isRecreational?: boolean;
        medicalId?: string;
        recreationalId?: string;
        monHours: string;
        tuesHours: string;
        wedHours: string;
        thursHours: string;
        friHours: string;
        satHours: string;
        sunHours: string;
    };
    stateName?: string;
    onMenuClick: (type: string) => string;
}

// Helper to get state path from store address or state name
const getStatePath = (storeName: string, stateNameProp?: string, address?: string): string => {
    // If state name is provided directly, use it
    if (stateNameProp) {
        return stateNameProp.toLowerCase().replace(/ /g, '-');
    }
    
    // Otherwise, try to extract from address
    if (address) {
        const stateAbbreviations: Record<string, string> = {
            'CT': 'connecticut',
            'MD': 'maryland',
            'NJ': 'new-jersey',
            'RI': 'rhode-island',
            'VT': 'vermont'
        };
        
        // Extract state abbreviation from address (e.g., "CT 06905")
        const stateMatch = address.match(/,\s*([A-Z]{2})\s*\d{5}/);
        if (stateMatch && stateAbbreviations[stateMatch[1]]) {
            return stateAbbreviations[stateMatch[1]];
        }
    }
    
    // Fallback: map store names to states
    const storeToState: Record<string, string> = {
        'STAMFORD': 'connecticut',
        'WEST HARTFORD': 'connecticut',
        'OLNEY': 'maryland',
        'VOORHEES': 'new-jersey',
        'EXETER': 'rhode-island',
        'ESSEX JUNCTION': 'vermont',
        'MOUNT OLIVE': 'new-jersey'
    };
    
    return storeToState[storeName.toUpperCase()] || 'unknown';
};

const DispensaryInfo: React.FC<StoreProps> = ({ store, stateName, onMenuClick }) => {
    const navigate = useNavigate();
    // Determine which store hours document to fetch
    const storeId = useMemo(() => {
      // For stores with both MED and REC, default to MED hours
      // (they should be the same anyway)
      // Use the menu ID directly as the store ID for store hours
      if (store.isMedical && store.medicalId) {
        return store.medicalId;
      } else if (store.isRecreational && store.recreationalId) {
        return store.recreationalId;
      }
      return null;
    }, [store]);

    const { storeHours, loading, error } = useStoreHours(storeId);

    // Popup alert state
    const [popupAlert, setPopupAlert] = useState<{ message: string } | null>(null);

    useEffect(() => {
      const fetchAlert = async () => {
        const id = store.medicalId || store.recreationalId;
        if (id) {
          const alert = await getPopupAlertForStore(id);
          setPopupAlert(alert);
        }
      };
      fetchAlert();
    }, [store.medicalId, store.recreationalId]);

    // Format hours for display - fallback to hardcoded if Firestore fails
    const displayHours = useMemo(() => {
      if (storeHours?.hours && !error) {
        return formatWeeklyHours(storeHours.hours, storeHours.temporaryClosures);
      }
      // Fallback to hardcoded hours when error or no data
      return {
        monHours: store.monHours,
        tuesHours: store.tuesHours,
        wedHours: store.wedHours,
        thursHours: store.thursHours,
        friHours: store.friHours,
        satHours: store.satHours,
        sunHours: store.sunHours
      };
    }, [storeHours, store, error]);

    return (
      <div className="dispensary-container">
        {popupAlert && (
          <div className="dispensary-alert">
            {popupAlert.message}
          </div>
        )}
        <div className="dispensary-content">
          <div className="dispensary-left-column">
            <div className="dispensary-header">
              <div className="dispensary-name">{store.name}</div>
              <p>{store.address}</p>
              <p>{store.phone}</p>
            </div>
            <div className="dispensary-menu-button-container" style={{ display: 'flex', flexDirection: 'column' }}>
              {store.isMedical && (
              <button
                className="dispensary-menu-button medical"
                onClick={() => {
                  const statePath = getStatePath(store.name, stateName, store.address);
                  const storePath = store.name.toLowerCase().replace(/ /g, '-');
                  const newPath = `/store/${statePath}/${storePath}/dispensary-medical-menu`;
                  
                  analyticsService.trackNavigation({
                    link_text: 'MEDICAL MENU',
                    destination: newPath,
                    source_page: window.location.pathname,
                    source_section: 'dispensary_info'
                  });
                  
                  analyticsService.trackEvent('menu_interaction', {
                    interaction_type: 'menu_button_click',
                    menu_type: 'medical',
                    store_name: store.name,
                    store_id: store.medicalId,
                    page_path: window.location.pathname
                  });
                  
                  navigate(newPath);
                }}
              >
                MEDICAL MENU
              </button>
              )}
              {store.isRecreational && (
              <button
                className="dispensary-menu-button recreational"
                onClick={() => {
                  const statePath = getStatePath(store.name, stateName, store.address);
                  const storePath = store.name.toLowerCase().replace(/ /g, '-');
                  const newPath = `/store/${statePath}/${storePath}/dispensary-recreational-menu`;
                  
                  analyticsService.trackNavigation({
                    link_text: 'RECREATIONAL MENU',
                    destination: newPath,
                    source_page: window.location.pathname,
                    source_section: 'dispensary_info'
                  });
                  
                  analyticsService.trackEvent('menu_interaction', {
                    interaction_type: 'menu_button_click',
                    menu_type: 'recreational',
                    store_name: store.name,
                    store_id: store.recreationalId,
                    page_path: window.location.pathname
                  });
                  
                  navigate(newPath);
                }}
              >
                RECREATIONAL MENU
              </button>
              )}
            </div>
          </div>
          <div className="dispensary-right-column">
            <div className="dispensary-hours">
              <div className="dispensary-hours-header"></div>
              {loading ? (
                <p style={{color: '#666', fontStyle: 'italic'}}>Loading hours...</p>
              ) : (
                <>
                  <p>{displayHours.monHours}</p>
                  <p>{displayHours.tuesHours}</p>
                  <p>{displayHours.wedHours}</p>
                  <p>{displayHours.thursHours}</p>
                  <p>{displayHours.friHours}</p>
                  <p>{displayHours.satHours}</p>
                  <p>{displayHours.sunHours}</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
};
  
export default DispensaryInfo;
